import { useHandler } from "@chatbotgang/etude/react/useHandler";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import type { SxProps } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ExternalLink } from "@/components/ExternalLink";
import { InfoBox } from "@/components/InfoBox";
import { Typography } from "@/components/Typography";

const LINE_DEVELOPERS_CONSOLE_URL = "https://developers.line.biz/console/";

const styles = {
  layout: {
    display: "flex",
    gap: "24px",
    alignItems: "flex-start",
  },
  layoutNarrow: {
    flexDirection: "column",
    alignItems: "stretch",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    flex: 1,
    minWidth: 0,
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  label: {
    fontSize: "0.75rem",
    fontWeight: 500,
    color: theme.colors.staticFgBody,
  },
  required: {
    color: theme.colors.staticFgNegative,
    marginLeft: "2px",
  },
  guide: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    flex: 1,
    minWidth: 0,
    padding: "16px",
    borderRadius: "8px",
    backgroundColor: theme.colors.staticBgPlain,
  },
  steps: {
    margin: 0,
    paddingLeft: "20px",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

type TextFieldChangeHandler = React.ComponentProps<
  typeof TextField
>["onChange"];

interface LineCreateFormProps {
  /** Puts the reference panel below the fields instead of beside them. */
  narrow: boolean;
  channelId: string;
  channelSecret: string;
  /** Server verdict shown under the ID field; empty when there is none. */
  channelIdError: string;
  /** Server verdict shown under the secret field; empty when there is none. */
  channelSecretError: string;
  disabled: boolean;
  onChannelIdChange: (value: string) => void;
  onChannelSecretChange: (value: string) => void;
}

/**
 * The two Messaging API credentials, with a panel showing where to find them
 * in the LINE Developers console.
 */
export const LineCreateForm: React.FC<LineCreateFormProps> =
  function LineCreateForm({
    narrow,
    channelId,
    channelSecret,
    channelIdError,
    channelSecretError,
    disabled,
    onChannelIdChange,
    onChannelSecretChange,
  }) {
    const { t } = useTranslation();

    const handleChannelIdChange = useHandler<TextFieldChangeHandler>((e) => {
      onChannelIdChange(e?.target.value ?? "");
    });

    const handleChannelSecretChange = useHandler<TextFieldChangeHandler>(
      (e) => {
        onChannelSecretChange(e?.target.value ?? "");
      },
    );

    return (
      <Box
        data-test="line-create-layout"
        sx={{ ...styles.layout, ...(narrow && styles.layoutNarrow) }}
      >
        <Box sx={styles.form}>
          <InfoBox
            variant="warning"
            description={t(
              "channels.line.create.messagingApiReminder",
              "Choose a Messaging API channel, not a LINE Login channel.",
            )}
          />
          <Box sx={styles.field}>
            <Box component="label" htmlFor="line-channel-id" sx={styles.label}>
              {t("channels.line.create.channelId.label", "Channel ID")}
              <Box component="span" sx={styles.required}>
                *
              </Box>
            </Box>
            <TextField
              id="line-channel-id"
              fullWidth
              size="small"
              autoComplete="off"
              placeholder={t(
                "channels.line.create.channelId.placeholder",
                "e.g. 1651234567",
              )}
              value={channelId}
              onChange={handleChannelIdChange}
              error={channelIdError !== ""}
              helperText={channelIdError || undefined}
              disabled={disabled}
            />
          </Box>
          <Box sx={styles.field}>
            <Box
              component="label"
              htmlFor="line-channel-secret"
              sx={styles.label}
            >
              {t("channels.line.create.channelSecret.label", "Channel secret")}
              <Box component="span" sx={styles.required}>
                *
              </Box>
            </Box>
            <ChannelSecretField
              value={channelSecret}
              onChange={handleChannelSecretChange}
              error={channelSecretError}
              disabled={disabled}
            />
          </Box>
        </Box>
        <LineCredentialsGuide />
      </Box>
    );
  };

interface ChannelSecretFieldProps {
  value: string;
  onChange: TextFieldChangeHandler;
  error: string;
  disabled: boolean;
}

/** A masked input with a show/hide toggle; hidden again on every mount. */
const ChannelSecretField: React.FC<ChannelSecretFieldProps> =
  function ChannelSecretField({ value, onChange, error, disabled }) {
    const { t } = useTranslation();
    const [visible, setVisible] = useState(false);

    const toggleVisible = useHandler(() => {
      setVisible((current) => !current);
    });

    return (
      <TextField
        id="line-channel-secret"
        fullWidth
        size="small"
        type={visible ? "text" : "password"}
        // Keeps password managers from offering a saved login here.
        autoComplete="new-password"
        value={value}
        onChange={onChange}
        error={error !== ""}
        helperText={error || undefined}
        disabled={disabled}
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  edge="end"
                  onClick={toggleVisible}
                  aria-label={
                    visible
                      ? t(
                          "channels.line.create.channelSecret.hide",
                          "Hide secret",
                        )
                      : t(
                          "channels.line.create.channelSecret.show",
                          "Show secret",
                        )
                  }
                >
                  {visible ? (
                    <VisibilityOff fontSize="small" />
                  ) : (
                    <Visibility fontSize="small" />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
      />
    );
  };

const LineCredentialsGuide: React.FC = function LineCredentialsGuide() {
  const { t } = useTranslation();
  return (
    <Box component="section" data-test="line-create-guide" sx={styles.guide}>
      <Typography
        variant="body"
        fontWeight={700}
        color={theme.colors.staticFgTitle}
      >
        {t("channels.line.create.guide.title", "Where to find these values")}
      </Typography>
      <Box component="ol" sx={styles.steps}>
        <Box component="li">
          <Typography variant="body" color={theme.colors.staticFgBody}>
            {t(
              "channels.line.create.guide.step1",
              "Log in to LINE Developers and select your provider.",
            )}{" "}
            <ExternalLink href={LINE_DEVELOPERS_CONSOLE_URL} trailingIcon>
              {t("channels.line.create.guide.consoleLink", "LINE Developers")}
            </ExternalLink>
          </Typography>
        </Box>
        <Box component="li">
          <Typography variant="body" color={theme.colors.staticFgBody}>
            {t(
              "channels.line.create.guide.step2",
              "Open the Messaging API channel of this official account.",
            )}
          </Typography>
        </Box>
        <Box component="li">
          <Typography variant="body" color={theme.colors.staticFgBody}>
            {t(
              "channels.line.create.guide.step3",
              "Find the Channel ID and Channel secret on the Basic settings tab.",
            )}
          </Typography>
        </Box>
      </Box>
      <ExternalLink
        href={t(
          "channels.line.create.guide.tutorialUrl",
          "https://crescendolab.zendesk.com/hc/zh-tw",
        )}
        trailingIcon
      >
        {t("channels.line.create.guide.tutorial", "Full guide")}
      </ExternalLink>
    </Box>
  );
};
